import React, { useState, useRef, useCallback } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm', minzoom: 0, maxzoom: 19 }],
};

const isMoving = (speed) => (speed ?? 0) > 0.5;

const PositionMap = ({ data, tamanio }) => {
  const lat = parseFloat(data?.latitud);
  const lng = parseFloat(data?.longitud);
  const speed   = data?.velocidad_kmh ?? 0;
  const battery = data?.bateria ?? null;
  const signal  = data?.ultima_señal_gps ?? null;
  const moving  = isMoving(speed);

  const [viewState, setViewState] = useState({ longitude: lng, latitude: lat, zoom: 14 });
  const mapRef = useRef(null);

  const onMapLoad = useCallback(() => {
    mapRef.current?.flyTo({ center: [lng, lat], zoom: 14, duration: 700 });
  }, [lat, lng]);

  const containerStyle = tamanio || { width: '100%', height: '240px' };

  if (isNaN(lat) || isNaN(lng)) {
    return (
      <div style={{
        ...containerStyle, background: '#f3f4f6', display: 'flex',
        alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: '#9ca3af',
      }}>
        Posición no disponible
      </div>
    );
  }

  const pinColor = moving ? '#22c55e' : '#ef4444';

  return (
    <>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={e => setViewState(e.viewState)}
        style={containerStyle}
        mapStyle={OSM_STYLE}
        onLoad={onMapLoad}
      >
        <NavigationControl position="top-right" />

        <Marker longitude={lng} latitude={lat} anchor="center">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Pulse ring */}
            <div style={{
              position: 'absolute',
              width: 46, height: 46, borderRadius: '50%',
              background: pinColor,
              opacity: 0.25,
              animation: 'pos-pulse 1.8s ease-out infinite',
            }} />
            <div style={{
              position: 'absolute',
              width: 34, height: 34, borderRadius: '50%',
              background: pinColor,
              opacity: 0.18,
              animation: 'pos-pulse 1.8s ease-out infinite',
              animationDelay: '0.4s',
            }} />
            {/* Pin dot */}
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: pinColor,
              border: '3px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              position: 'relative', zIndex: 1,
            }} />
          </div>
        </Marker>
      </Map>

      {/* Info bar below the map */}
      <div style={{
        display: 'flex', gap: 8, padding: '8px 10px',
        background: '#f8fafc', borderTop: '1px solid #e2e8f0',
        flexWrap: 'wrap',
      }}>
        <InfoChip
          icon="🚚"
          label={moving ? 'En movimiento' : 'Detenido'}
          color={moving ? '#15803d' : '#b91c1c'}
          bg={moving ? '#dcfce7' : '#fee2e2'}
        />
        <InfoChip icon="⚡" label={`${speed.toFixed(1)} km/h`} />
        {battery != null && <InfoChip icon="🔋" label={`${battery}%`} />}
        {signal && <InfoChip icon="📡" label={signal} />}
      </div>

      <style>{`
        @keyframes pos-pulse {
          0%   { transform: scale(0.8); opacity: 0.3; }
          70%  { transform: scale(1.6); opacity: 0;   }
          100% { transform: scale(0.8); opacity: 0;   }
        }
      `}</style>
    </>
  );
};

const InfoChip = ({ icon, label, color = '#374151', bg = '#f1f5f9' }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 9999,
    background: bg, color, fontSize: 11, fontWeight: 600,
    fontFamily: 'monospace',
  }}>
    {icon} {label}
  </span>
);

export default PositionMap;
